/**
 * Il Cruscotto diventa la vista d'apertura di Fabio (SPEC §4.8).
 *
 * Sei risposte, **in quest'ordine**, perché è l'ordine in cui se le fa lui la
 * mattina:
 *
 *  1. quanto c'è da produrre oggi, per macchina;
 *  2. cosa è stato prodotto ieri, e con quale scostamento;
 *  3. dove è ferma la merce prodotta e non ancora a magazzino;
 *  4. il carico delle prossime settimane;
 *  5. le anomalie, separate per tipo;
 *  6. a che ora è aggiornato tutto questo — che sta in cima, perché è la
 *     condizione per fidarsi di tutto il resto.
 *
 * ## La regola grafica che vale su tutta la pagina
 *
 * «Il sistema non deve mai presentare una stima con lo stesso peso visivo di un
 * dato certo» (§4.4). Qui si traduce in una cosa sola e costante: **le stime
 * stanno su fondo tratteggiato e portano la parola "stima"**. La merce in
 * transito è l'unico numero stimato di questa pagina, e si vede.
 *
 * ## Cosa questa pagina ancora non sa dire
 *
 * Il **ritardo di ritiro** — merce pronta che il cliente non è venuto a
 * prendere — non è misurato. Il ritardo mostrato è solo quello di **produzione**,
 * ed è etichettato come tale: §4.5 vieta di sommarli, e il modo più sicuro per
 * non sommarli è non far finta di avere il secondo.
 */
import {
  AlertTriangle,
  CalendarDays,
  ChevronDown,
  CheckCircle2,
  Factory,
  Info,
  Loader2,
  PackageSearch,
  TrendingUp,
} from "lucide-react";
import { useState } from "react";

import { RigaTesta } from "@/components/RigaTesta";
import { useM2CruscottoDirezione } from "@/hooks/useM2";
import { cn } from "@/lib/utils";
import { fmtData, num } from "@/pages/M2/format";
import type {
  AvanzamentoResponse,
  DueRitardiResponse,
  FasciaAnzianita,
  RitardoProduzione,
  RitardoRitiro,
  CaricoSettimana,
  ConsuntivoResponse,
  CruscottoDirezioneResponse,
  ScostamentoRead,
  TransitoResponse,
} from "@/types/m2";

const GIORNI_ISO = ["", "lun", "mar", "mer", "gio", "ven", "sab", "dom"];

export function CruscottoDirezione({
  onVai,
}: {
  onVai: (sezione: "piano" | "caricamenti" | "fabbisogno") => void;
}) {
  const { data, isLoading } = useM2CruscottoDirezione();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-24 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Caricamento…
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-4">
      {/* 6. A che ora è aggiornato tutto questo. */}
      <RigaTesta
        contesto="produzione e piano a confronto"
        caricatoIl={data.aggiornato_il}
        etichettaData="magazzino aggiornato al"
        titoloBadge="Tutti i numeri di produzione di questa pagina vengono dall'export di magazzino: più invecchia, meno raccontano oggi"
      >
        {/* ⚠️ `fmtData(null)` rende «—», e «piano del —» si legge come un dato
            che non è arrivato invece che come un piano che non è mai stato
            generato: è lo stato normale di questa fase, e va detto così. */}
        <span className="text-[11.5px] text-muted-foreground">
          {data.piano_generato_il
            ? `piano del ${fmtData(data.piano_generato_il)}`
            : "nessun piano proposto"}{" "}
          · fabbisogno del {fmtData(data.fabbisogno_caricato_il)}
        </span>
      </RigaTesta>

      {data.ritardi && <DueRitardi r={data.ritardi} />}
      {data.anomalie.length > 0 && <Anomalie anomalie={data.anomalie} />}
      {data.avanzamento && <Oggi a={data.avanzamento} onVai={() => onVai("piano")} />}
      {data.ieri && <Ieri c={data.ieri} />}
      {data.in_transito && <InTransito t={data.in_transito} />}
      <CaricoProspettico settimane={data.carico_settimane} onVai={() => onVai("piano")} />
      {data.calendario?.disponibile && <Calendario c={data.calendario} />}
    </div>
  );
}

/**
 * I due ritardi, affiancati e mai sommati (SPEC §4.5).
 *
 * Due riquadri, e in mezzo la frase che spiega perché non c'è un terzo numero.
 * Non è una scelta grafica: è la sostanza del requisito. «Oggi il modulo tratta
 * ogni data di consegna come tassativa, e quindi conta come ritardo anche la
 * merce pronta a magazzino che il cliente non è venuto a prendere.»
 *
 * Sui dati veri del 14-09 i due numeri sono 7 e 456: tenerli insieme voleva dire
 * guardare un numero governato quasi per intero da qualcosa che la produzione
 * non controlla.
 */
function DueRitardi({ r }: { r: DueRitardiResponse }) {
  return (
    <section>
      <Titolo icona={AlertTriangle}>I due ritardi — misure diverse, mai sommate</Titolo>
      <div className="grid items-start gap-2 lg:grid-cols-2">
        <RiquadroRitardo
          titolo="Ritardo di produzione"
          definizione={r.produzione.definizione}
          righe={r.produzione.righe}
          massimo={r.produzione.giorni_massimo}
          fonte={r.produzione.fonte}
          escluse={r.produzione.a_chiamata_escluse}
          nota={r.produzione.nota_a_chiamata}
          distribuzione={r.produzione.distribuzione}
          tono="produzione"
          dettaglio={<DettaglioProduzione r={r.produzione} />}
        />
        <RiquadroRitardo
          titolo="Ritardo di ritiro"
          definizione={r.ritiro.definizione}
          righe={r.ritiro.righe}
          massimo={r.ritiro.giorni_massimo}
          fonte={r.ritiro.fonte}
          escluse={r.ritiro.a_chiamata_escluse}
          nota={r.ritiro.nota_a_chiamata}
          distribuzione={r.ritiro.distribuzione}
          extra={`${r.ritiro.clienti} clienti · ${num(r.ritiro.pezzi_fermi)} pezzi fermi`}
          tono="ritiro"
          dettaglio={<DettaglioRitiro r={r.ritiro} />}
        />
      </div>
      <p className="mt-1.5 text-[11.5px] text-muted-foreground">{r.avvertenza}</p>
      {r.ritiro.nota_marcature && (
        <p className="mt-1 text-[11.5px] text-amber-700 dark:text-amber-400">
          {r.ritiro.nota_marcature}
        </p>
      )}
    </section>
  );
}

/**
 * ⚠️ Niente «giorni in tutto».
 *
 * Sommare ritardi che corrono **in parallelo** non dà una durata: dà una
 * grandezza «giorni per righe» con l'etichetta «giorni» sopra — nessuno ha
 * aspettato 14.636 giorni. E cresce di 456 al giorno da sola, senza che sia
 * successo niente: un indicatore che peggiora mentre dormi non si può usare.
 *
 * Al suo posto: **quante righe**, **il caso peggiore**, e la distribuzione per
 * fasce, che dice la forma del problema dove la somma diceva solo un numero.
 */
function RiquadroRitardo({
  titolo,
  definizione,
  righe,
  massimo,
  fonte,
  escluse,
  nota,
  distribuzione,
  extra,
  tono,
  dettaglio,
}: {
  titolo: string;
  definizione: string;
  righe: number;
  massimo: number;
  fonte: string;
  escluse: number;
  nota: string | null;
  distribuzione: FasciaAnzianita[];
  extra?: string;
  tono: "produzione" | "ritiro";
  dettaglio: React.ReactNode;
}) {
  const [aperto, setAperto] = useState(false);
  return (
    <div
      className={cn(
        "rounded-lg border p-3.5",
        // Aperto si prende tutta la riga: affiancato lasciava mezzo schermo
        // vuoto accanto a un elenco di quaranta clienti. Visto guardandolo.
        aperto && "lg:col-span-2",
        tono === "produzione" ? "border-brand-200 bg-accent/40 dark:border-brand-800" : "bg-card"
      )}
    >
      <div className="text-[12.5px] font-semibold">{titolo}</div>
      <div className="mt-0.5 text-[11.5px] text-muted-foreground">{definizione}</div>
      <div className="mt-2 flex flex-wrap items-baseline gap-x-3">
        <span className="text-2xl font-semibold tabular-nums">{num(righe)}</span>
        <span className="text-[12px] text-muted-foreground">
          righe · il più vecchio <b className="text-foreground">{massimo} giorni</b>
          {extra && ` · ${extra}`}
        </span>
      </div>

      {/* La distribuzione per fasce: è ciò che sostituisce la somma. */}
      {righe > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {distribuzione
            .filter((f) => f.righe > 0)
            .map((f) => (
              <span
                key={f.fascia}
                className={cn(
                  "rounded border px-1.5 py-0.5 text-[10.5px]",
                  f.da > 90
                    ? "border-red-200 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300"
                    : f.da > 30
                      ? "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950/50 dark:text-amber-300"
                      : "text-muted-foreground"
                )}
              >
                {f.righe} · {f.fascia}
              </span>
            ))}
        </div>
      )}

      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 text-[11px] text-muted-foreground">
        <span>Si misura da: {fonte}.</span>
        {righe > 0 && (
          <button
            type="button"
            onClick={() => setAperto((v) => !v)}
            className="ml-auto inline-flex items-center gap-1 font-medium text-primary hover:opacity-80"
          >
            {aperto ? "Chiudi il dettaglio" : "Vedi il dettaglio"}
            <ChevronDown className={cn("size-3.5 transition-transform", aperto && "rotate-180")} />
          </button>
        )}
      </div>
      {escluse > 0 && nota && <div className="mt-1 text-[11px] text-muted-foreground">{nota}</div>}
      {aperto && <div className="mt-3 border-t pt-3">{dettaglio}</div>}
    </div>
  );
}

/** Sette righe non si raggruppano e non si paginano: si leggono. */
function DettaglioProduzione({ r }: { r: RitardoProduzione }) {
  if (r.voci.length === 0) return null;
  return (
    <div className="space-y-1">
      {r.voci.map((v, i) => (
        <div
          key={`${v.codice_articolo}-${i}`}
          className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[12px]"
        >
          <span className="w-12 shrink-0 text-right font-semibold tabular-nums text-amber-700 dark:text-amber-400">
            {v.giorni} gg
          </span>
          <span className="font-mono text-[11px]">{v.codice_articolo}</span>
          <span className="min-w-0 flex-1 truncate text-muted-foreground">{v.descrizione}</span>
          <span className="shrink-0 tabular-nums text-muted-foreground">
            {num(v.quantita ?? 0)} {v.um}
          </span>
          <span className="shrink-0 text-[10.5px] text-muted-foreground">
            chiesto il {fmtData(v.data_richiesta)}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * 456 righe non si leggono; **41 clienti** sì.
 *
 * Il raggruppamento non è un modo di paginare: è la domanda giusta. Chi guarda
 * questo numero non si chiede «quali righe» ma «chi mi sta fermo, e da quanto».
 * Ordinati dal più vecchio, come chiesto da Enrico il 14-09.
 */
const MAX_RIGHE_CLIENTE = 12;

function DettaglioRitiro({ r }: { r: RitardoRitiro }) {
  const [soglia, setSoglia] = useState(0);
  const [apertoCliente, setApertoCliente] = useState<string | null>(null);

  const clienti = r.per_cliente.filter((c) => c.giorni_massimo >= soglia);
  const filtri: [number, string][] = [
    [0, "tutti"],
    [31, "oltre 30 giorni"],
    [91, "oltre 90 giorni"],
  ];

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1.5">
        {filtri.map(([s, label]) => (
          <button
            key={s}
            type="button"
            onClick={() => setSoglia(s)}
            className={cn(
              "rounded-md border px-2 py-0.5 text-[11px] font-medium",
              soglia === s ? "border-primary bg-accent" : "bg-card hover:bg-accent/40"
            )}
          >
            {label}
          </button>
        ))}
        <span className="text-[11px] text-muted-foreground">
          {clienti.length} client{clienti.length === 1 ? "e" : "i"}
        </span>
      </div>

      <div className="overflow-hidden rounded-md border">
        {clienti.map((c) => {
          const chiave = String(c.cliente_codice ?? c.cliente);
          const aperto = apertoCliente === chiave;
          const tutte = r.voci.filter((v) => String(v.cliente_codice ?? v.cliente) === chiave);
          // ⚠️ Un cliente con 64 righe aperte occupa mezza pagina e seppellisce
          // gli altri quaranta. Se ne mostrano dodici e si dichiara il resto,
          // come ovunque nel modulo: il dettaglio riga per riga è materia di
          // Spedizioni, qui serve capire chi e da quanto.
          const righe = tutte.slice(0, MAX_RIGHE_CLIENTE);
          return (
            <div key={chiave} className="border-t first:border-t-0">
              <button
                type="button"
                onClick={() => setApertoCliente(aperto ? null : chiave)}
                className="flex w-full flex-wrap items-center gap-x-2.5 px-2.5 py-1.5 text-left text-[12px] hover:bg-accent/40"
              >
                <span
                  className={cn(
                    "w-12 shrink-0 text-right font-semibold tabular-nums",
                    c.giorni_massimo > 90
                      ? "text-red-700 dark:text-red-400"
                      : "text-amber-700 dark:text-amber-400"
                  )}
                >
                  {c.giorni_massimo} gg
                </span>
                <span className="min-w-0 flex-1 truncate font-medium">{c.cliente ?? "—"}</span>
                <span className="shrink-0 tabular-nums text-muted-foreground">
                  {c.righe} righe · {c.ordini} ordini · {num(c.pezzi)} pz
                </span>
                <ChevronDown
                  className={cn(
                    "size-3.5 shrink-0 text-muted-foreground transition-transform",
                    aperto && "rotate-180"
                  )}
                />
              </button>
              {aperto && (
                <div className="space-y-0.5 bg-muted/40 px-2.5 py-2">
                  {tutte.length > righe.length && (
                    <p className="pb-1 text-[11px] text-amber-700 dark:text-amber-400">
                      Ne vedi {righe.length} di {c.righe}: il resto in Spedizioni.
                    </p>
                  )}
                  {righe.length === 0 ? (
                    // ⚠️ L'elenco delle righe è tagliato a 200 mentre i clienti
                    // sono tutti: se un cliente sta oltre il taglio lo si dice,
                    // invece di mostrare un cliente che si apre sul vuoto.
                    <p className="text-[11px] text-muted-foreground">
                      Le righe di questo cliente sono oltre le prime {num(r.voci.length)}{" "}
                      caricate. Il dettaglio riga per riga sta in Spedizioni.
                    </p>
                  ) : (
                    righe.map((v, i) => (
                      <div
                        key={`${v.ordine_numero}-${v.codice_articolo}-${i}`}
                        className="flex flex-wrap items-center gap-x-2.5 text-[11.5px]"
                      >
                        <span className="w-10 shrink-0 text-right tabular-nums text-muted-foreground">
                          {v.giorni} gg
                        </span>
                        <span className="shrink-0 text-muted-foreground">
                          #{v.ordine_numero}
                        </span>
                        <span className="font-mono text-[11px]">{v.codice_articolo}</span>
                        <span className="min-w-0 flex-1 truncate text-muted-foreground">
                          {v.descrizione}
                        </span>
                        <span className="shrink-0 tabular-nums">{num(v.quantita)} pz</span>
                        {v.parziale && (
                          <span className="shrink-0 text-[10px] text-muted-foreground">
                            parziale
                          </span>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-muted-foreground">
        Qui si legge <b>chi</b> e <b>da quanto</b>. Per agire su un cliente che non ritira si
        passa da Spedizioni: è lì che vivono gli avvisi e le note di prelievo.
      </p>
    </div>
  );
}

// ── 5. le anomalie, in cima perché sono quelle che fanno agire ───────

function Anomalie({ anomalie }: { anomalie: CruscottoDirezioneResponse["anomalie"] }) {
  return (
    <section className="space-y-2">
      {anomalie.map((a) => (
        <div
          key={a.codice}
          className={cn(
            "flex gap-2.5 rounded-lg border px-3.5 py-3 text-[12.5px]",
            // La stima non ha lo stesso peso visivo del dato certo: bordo
            // tratteggiato e niente colore d'allarme.
            a.ipotesi
              ? "border-dashed bg-muted/40"
              : "border-amber-300 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/40"
          )}
        >
          {a.ipotesi ? (
            <PackageSearch className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
          ) : (
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-700 dark:text-amber-400" />
          )}
          <div>
            <div className="font-semibold">
              {a.titolo}
              {a.ipotesi && (
                <span className="ml-2 rounded-full border border-dashed px-1.5 py-0.5 text-[10px] font-normal text-muted-foreground">
                  stima
                </span>
              )}
            </div>
            <div className="mt-0.5 text-muted-foreground">{a.dettaglio}</div>
          </div>
        </div>
      ))}
    </section>
  );
}

// ── la riga di una linea, che si apre sul suo dettaglio ──────────────
//
// OGGI e IERI mostrano la stessa cosa da due lati — cosa c'è da fare, cosa è
// stato fatto — e la domanda che segue è la stessa: «di che roba si tratta?».
// Prima bisognava uscire dal cruscotto per saperlo. Ora la riga si apre sotto,
// come i due ritardi.

function ArticoliLinea({
  articoli,
  misurato,
}: {
  articoli: ScostamentoRead[];
  misurato: boolean;
}) {
  if (articoli.length === 0) {
    return (
      <p className="px-3.5 pb-2.5 pl-8 text-[11.5px] text-muted-foreground">
        Nessun articolo da mostrare per questa linea.
      </p>
    );
  }
  return (
    <div className="bg-muted/40 px-3.5 pb-2.5 pl-8 pt-1">
      <table className="w-full text-[11.5px]">
        <thead className="text-muted-foreground">
          <tr className="text-left">
            <th className="py-1 font-normal">Articolo</th>
            <th className="py-1 text-right font-normal">Pianificati</th>
            {/* Se il magazzino non ha fotografato il giorno, le due colonne del
                confronto sono quindici righe di «non misurato» e altrettanti
                trattini: la riga della linea lo dice già una volta, e una volta
                basta. */}
            {misurato && (
              <>
                <th className="py-1 text-right font-normal">Prodotti</th>
                <th className="py-1 text-right font-normal">Scostamento</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {articoli.map((a) => (
            <tr key={a.chiave} className="border-t border-border/60">
              <td className="py-1 pr-3">
                <span className="font-medium">{a.chiave}</span>
                {a.etichetta && (
                  <span className="ml-1.5 text-muted-foreground">{a.etichetta}</span>
                )}
              </td>
              <td className="py-1 text-right tabular-nums">
                {a.pianificato === null ? (
                  <span className="text-muted-foreground">—</span>
                ) : (
                  num(a.pianificato)
                )}
              </td>
              {misurato && (
                <>
                  <td className="py-1 text-right tabular-nums">
                    {a.prodotto !== null ? (
                      num(a.prodotto)
                    ) : (
                      <span className="text-muted-foreground">non misurato</span>
                    )}
                  </td>
                  <td
                    className={cn(
                      "py-1 text-right tabular-nums font-medium",
                      a.scostamento === null
                        ? "text-muted-foreground"
                        : a.scostamento < 0
                          ? "text-amber-700 dark:text-amber-400"
                          : a.scostamento > 0
                            ? "text-brand-700 dark:text-brand-400"
                            : "text-muted-foreground"
                    )}
                  >
                    {a.scostamento !== null
                      ? `${a.scostamento > 0 ? "+" : ""}${num(a.scostamento)}`
                      : "—"}
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RigaLinea({
  codice,
  articoli,
  misurato,
  children,
}: {
  codice: string;
  articoli: ScostamentoRead[];
  misurato: boolean;
  children: React.ReactNode;
}) {
  const [aperto, setAperto] = useState(false);
  return (
    <div>
      <button
        type="button"
        onClick={() => setAperto((v) => !v)}
        aria-expanded={aperto}
        className="flex w-full items-center gap-3 px-3.5 py-2 text-left text-[12.5px] hover:bg-muted/50"
      >
        <ChevronDown
          className={cn(
            "size-3.5 shrink-0 text-muted-foreground transition-transform",
            aperto && "rotate-180"
          )}
        />
        <span className="w-20 shrink-0 font-medium">{codice}</span>
        {children}
      </button>
      {aperto && <ArticoliLinea articoli={articoli} misurato={misurato} />}
    </div>
  );
}

// ── 1. quanto c'è da produrre oggi ───────────────────────────────────

function Oggi({ a, onVai }: { a: AvanzamentoResponse; onVai: () => void }) {
  return (
    <section>
      <Titolo icona={Factory}>Oggi</Titolo>
      <div className="rounded-lg border bg-card">
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 border-b px-3.5 py-3">
          <span className="text-2xl font-semibold tabular-nums">
            {num(a.oggi.pianificato)}
            <span className="ml-1 text-[12px] font-normal text-muted-foreground">
              pezzi da confezionare
            </span>
          </span>
          {/* ⚠️ «0%» e «non lo sappiamo» non si mostrano uguali: se il
              magazzino non ha ancora registrato oggi, non c'è nessuna
              percentuale da dare. */}
          {a.oggi.misurato ? (
            <span className="text-[12.5px] text-muted-foreground">
              prodotti finora <b className="text-foreground">{num(a.oggi.prodotto ?? 0)}</b>
              {a.oggi.avanzamento_pct !== null && ` · ${a.oggi.avanzamento_pct}%`}
            </span>
          ) : (
            <span className="text-[12.5px] text-muted-foreground">
              prodotti finora: <b className="text-foreground">non ancora misurato</b>
            </span>
          )}
          <button
            type="button"
            onClick={onVai}
            className="ml-auto text-[12px] font-medium text-primary hover:opacity-80"
          >
            apri il piano
          </button>
        </div>
        <div className="divide-y">
          {a.oggi.per_macchina.length === 0 ? (
            <p className="px-3.5 py-3 text-[12px] text-muted-foreground">
              Il piano non prevede confezionamento oggi.
            </p>
          ) : (
            a.oggi.per_macchina.map((m) => (
              <RigaLinea
                key={m.macchina}
                codice={m.macchina}
                articoli={m.articoli}
                misurato={a.oggi.misurato}
              >
                <span className="min-w-0 flex-1">
                  {a.oggi.misurato ? (
                    <Barra pct={m.avanzamento_pct ?? 0} />
                  ) : (
                    <span className="block h-1.5 w-full rounded-full border border-dashed" />
                  )}
                </span>
                <span className="shrink-0 tabular-nums text-muted-foreground">
                  {a.oggi.misurato ? `${num(m.prodotto ?? 0)} / ` : ""}
                  {m.pianificato === null ? "—" : num(m.pianificato)}
                </span>
              </RigaLinea>
            ))
          )}
        </div>
        <div className="flex flex-wrap items-center gap-x-3 border-t px-3.5 py-2 text-[11.5px] text-muted-foreground">
          <TrendingUp className="size-3.5" />
          {a.periodo.giorni_confrontabili === 0 ? (
            <span>
              Nel periodo {fmtData(a.periodo.da)} → {fmtData(a.periodo.a)} non c&apos;è nessuna
              giornata confrontabile col piano.
            </span>
          ) : (
            <span>
              Nelle {a.periodo.giorni_confrontabili} giornate confrontabili fra{" "}
              {fmtData(a.periodo.da)} e {fmtData(a.periodo.a)}:{" "}
              <b className="text-foreground">{num(a.periodo.prodotto)}</b> prodotti su{" "}
              {num(a.periodo.pianificato)} pianificati
              {a.periodo.avanzamento_pct !== null && ` (${a.periodo.avanzamento_pct}%)`}
            </span>
          )}
        </div>
        {a.oggi.nota && <Nota>{a.oggi.nota}</Nota>}
        {a.periodo.nota && <Nota>{a.periodo.nota}</Nota>}
        {a.avvertenza && <Nota>{a.avvertenza}</Nota>}
      </div>
    </section>
  );
}

function Barra({ pct }: { pct: number }) {
  return (
    <span className="block h-1.5 w-full overflow-hidden rounded-full bg-muted">
      <span
        className={cn(
          "block h-full rounded-full",
          pct >= 90 ? "bg-emerald-600" : pct >= 50 ? "bg-brand-600" : "bg-amber-500"
        )}
        style={{ width: `${Math.min(100, Math.max(2, pct))}%` }}
      />
    </span>
  );
}

// ── 2. cosa è stato prodotto ieri ────────────────────────────────────

function Ieri({ c }: { c: ConsuntivoResponse }) {
  return (
    <section>
      <Titolo icona={CheckCircle2}>Ieri — {fmtData(c.giorno)}</Titolo>
      <div className="rounded-lg border bg-card">
        {!c.confrontabile && (
          <div className="flex gap-2.5 border-b px-3.5 py-3 text-[12.5px] text-muted-foreground">
            <Info className="mt-0.5 size-4 shrink-0" />
            <div>{c.motivo}</div>
          </div>
        )}
        {/* Senza piano di riferimento le linee si mostrano lo stesso — con il
            solo prodotto — e si aprono lo stesso: quello che è uscito dalle
            macchine è un dato, non dipende dall'esserci del piano. */}
        <div className="divide-y">
          {c.per_macchina.map((m) => (
            <RigaLinea
              key={m.chiave}
              codice={m.chiave}
              articoli={m.articoli}
              misurato={c.misurato}
            >
              <span className="min-w-0 flex-1 text-muted-foreground">
                {c.confrontabile && m.pianificato !== null && `pianificati ${num(m.pianificato)} · `}
                {c.misurato && m.prodotto !== null
                  ? `prodotti ${num(m.prodotto)}`
                  : "prodotti: non ancora misurati"}
              </span>
              {/* ⚠️ Senza piano la colonna sparisce del tutto, non mostra «+4.158».
                  Il 19-09-2026 questa riga stampava tre scostamenti sotto la frase
                  «lo scostamento non si può calcolare»: erano `prodotto - 0`, con
                  lo zero che veniva dal piano mancante e non dal piano. */}
              {c.confrontabile && (
                <span
                  className={cn(
                    "shrink-0 tabular-nums font-medium",
                    m.scostamento === null
                      ? "text-muted-foreground"
                      : m.scostamento < 0
                        ? "text-amber-700 dark:text-amber-400"
                        : m.scostamento > 0
                          ? "text-brand-700 dark:text-brand-400"
                          : "text-muted-foreground"
                  )}
                >
                  {m.scostamento === null
                    ? "—"
                    : `${m.scostamento > 0 ? "+" : ""}${num(m.scostamento)}`}
                </span>
              )}
            </RigaLinea>
          ))}
        </div>
        {c.anomalie_scorta.filter((a) => !a.a_scorta).length > 0 && (
          <div className="border-t px-3.5 py-2.5 text-[12px]">
            <span className="font-medium">Prodotto più del pianificato, senza marcatura:</span>{" "}
            <span className="text-muted-foreground">
              {c.anomalie_scorta
                .filter((a) => !a.a_scorta)
                .slice(0, 5)
                .map((a) => `${a.codice} (+${num(a.differenza)})`)
                .join(" · ")}
            </span>
          </div>
        )}
        {c.nota_misura && <Nota>{c.nota_misura}</Nota>}
        {c.nota_attendibilita && <Nota>{c.nota_attendibilita}</Nota>}
      </div>
    </section>
  );
}

// ── 3. la merce in transito — l'unica stima della pagina ─────────────

function InTransito({ t }: { t: TransitoResponse }) {
  if (!t.disponibile) return null;
  return (
    <section>
      <Titolo icona={PackageSearch}>Merce prodotta che il magazzino non vede</Titolo>
      {/* Bordo tratteggiato e parola «stima»: §4.4 vieta di dare a una stima lo
          stesso peso visivo di un dato certo, e questa è l'unica stima qui. */}
      <div className="rounded-lg border border-dashed bg-muted/30 p-3.5">
        <div className="flex flex-wrap items-baseline gap-x-3">
          <span className="text-xl font-semibold tabular-nums">{num(t.quantita_totale)}</span>
          <span className="text-[12.5px] text-muted-foreground">
            pezzi su {t.righe} articoli, dal {fmtData(t.da_quando)}
            {t.giorni !== null && t.giorni > 0 && ` (${t.giorni} giorni)`}
          </span>
          <span className="rounded-full border border-dashed px-2 py-0.5 text-[10.5px] text-muted-foreground">
            stima
          </span>
        </div>
        {t.voci.length > 0 && (
          <div className="mt-2 space-y-0.5">
            {t.voci.slice(0, 6).map((v) => (
              <div key={v.codice_articolo} className="text-[12px]">
                <span className="font-mono text-[11.5px]">{v.codice_articolo}</span>{" "}
                <span className="text-muted-foreground">{v.descrizione}</span>{" "}
                <b>
                  {num(v.quantita)} {v.um}
                </b>
              </div>
            ))}
            {t.voci.length > 6 && (
              <div className="text-[11.5px] text-muted-foreground">
                e altri {t.voci.length - 6}.
              </div>
            )}
          </div>
        )}
        {t.avvertenza && (
          <p className="mt-2 text-[11.5px] text-muted-foreground">{t.avvertenza}</p>
        )}
      </div>
    </section>
  );
}

// ── 4. il carico delle prossime settimane ────────────────────────────

function CaricoProspettico({
  settimane,
  onVai,
}: {
  settimane: CaricoSettimana[];
  onVai: () => void;
}) {
  if (settimane.length === 0) return null;
  return (
    <section>
      <Titolo icona={CalendarDays}>Il carico delle prossime settimane</Titolo>
      <div className="overflow-hidden rounded-lg border bg-card">
        {settimane.map((s) => (
          <button
            key={s.settimana}
            type="button"
            onClick={onVai}
            className="flex w-full flex-wrap items-center gap-x-4 gap-y-1 border-t px-3.5 py-2.5 text-left text-[12.5px] first:border-t-0 hover:bg-accent/40"
          >
            <span className="w-28 shrink-0 font-medium">{s.etichetta}</span>
            {s.fasi.map((f) => (
              <span key={f.fase} className="flex min-w-[13rem] flex-1 items-center gap-2">
                <span className="w-24 shrink-0 text-[11px] text-muted-foreground">
                  {f.fase === "estrusione" ? "estrusione" : "confez."}
                </span>
                <span className="min-w-0 flex-1">
                  <Barra pct={f.saturazione_pct ?? 0} />
                </span>
                <span className="w-28 shrink-0 text-right tabular-nums text-muted-foreground">
                  {num(f.quantita)} {f.um}
                  {f.saturazione_pct !== null && (
                    <b
                      className={cn(
                        "ml-1",
                        f.saturazione_pct > 100
                          ? "text-red-700 dark:text-red-400"
                          : f.saturazione_pct > 80
                            ? "text-amber-700 dark:text-amber-400"
                            : "text-foreground"
                      )}
                    >
                      {f.saturazione_pct}%
                    </b>
                  )}
                </span>
              </span>
            ))}
          </button>
        ))}
      </div>
      <p className="mt-1 text-[11px] text-muted-foreground">
        La saturazione è il lavoro della settimana diviso la capacità delle linee attive nei
        giorni in cui si produce. Estrusione in kg, confezionamento in pezzi: non si sommano.
      </p>
    </section>
  );
}

// ── il calendario dedotto dalle etichette ────────────────────────────

function Calendario({ c }: { c: NonNullable<CruscottoDirezioneResponse["calendario"]> }) {
  const max = Math.max(...c.evidenza.map((e) => e.pallet), 1);
  return (
    <section className="rounded-lg border bg-muted/30 p-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <CalendarDays className="size-4 shrink-0 text-muted-foreground" />
        <span className="text-[12.5px] font-medium">
          Si produce {c.giorni.map((g) => GIORNI_ISO[g]).join(", ")}
        </span>
        <span className="text-[11.5px] text-muted-foreground">
          — non è una convenzione: è quello che dicono le etichette dei pallet, ed è il
          calendario che il piano rispetta.
        </span>
      </div>
      <div className="mt-2 flex items-end gap-1.5">
        {c.evidenza.map((e) => (
          <span key={e.giorno_iso} className="flex flex-col items-center gap-1">
            <span
              title={`${e.pallet} pallet su ${e.giornate} giornate`}
              className={cn(
                "w-7 rounded-sm",
                c.giorni.includes(e.giorno_iso) ? "bg-brand-600" : "bg-muted-foreground/30"
              )}
              style={{ height: `${Math.max(3, (e.pallet / max) * 28)}px` }}
            />
            <span className="text-[10px] text-muted-foreground">
              {GIORNI_ISO[e.giorno_iso]}
            </span>
          </span>
        ))}
      </div>
    </section>
  );
}

// ── pezzi comuni ─────────────────────────────────────────────────────

function Titolo({
  children,
  icona: Icona,
}: {
  children: React.ReactNode;
  icona?: React.ComponentType<{ className?: string }>;
}) {
  return (
    <h2 className="mb-2 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
      {Icona && <Icona className="size-3.5" />}
      {children}
    </h2>
  );
}

function Nota({ children }: { children: React.ReactNode }) {
  return (
    <p className="border-t px-3.5 py-2 text-[11px] text-muted-foreground">{children}</p>
  );
}
